import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDataStore } from '../stores/dataStore'
import { useAuthStore } from '../stores/authStore'
import InvoiceModule from '../components/finance/InvoiceModule'
import ReceiptModule from '../components/finance/ReceiptModule'
import QuotationModule from '../components/finance/QuotationModule'
import ReportModule from '../components/finance/ReportModule'
import PrintBudget from '../components/finance/PrintBudget'
import PrintExpense from '../components/finance/PrintExpense'
import { 
  DollarSign, 
  TrendingUp, 
  TrendingDown,
  Plus,
  Search,
  Calendar,
  FileText,
  Download,
  Receipt,
  X,
  Trash2,
  Edit
} from 'lucide-react'
import './Finance.css'

export default function Finance() {
  const [activeTab, setActiveTab] = useState('overview')
  const [showModal, setShowModal] = useState(false)
  const [modalType, setModalType] = useState('')
  const [selectedItem, setSelectedItem] = useState(null)
  const [formData, setFormData] = useState({})
  const [printBudget, setPrintBudget] = useState(null)
  const [printExpense, setPrintExpense] = useState(null)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState({ type: '', text: '' })
  const API_URL = import.meta.env.VITE_API_URL || 'https://www.secs.gismac.co.ke/api'
  const navigate = useNavigate()
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin';
  const { 
    budgets, 
    expenses, 
    financialSummary,
    fetchBudgets, 
    fetchExpenses, 
    fetchFinancialSummary 
  } = useDataStore()

  useEffect(() => {
    fetchBudgets()
    fetchExpenses()
    fetchFinancialSummary()
  }, [])

  const summary = financialSummary || {}

  const showMessage = (type, text) => { setMessage({ type, text }); setTimeout(() => setMessage({ type: "", text: "" }), 5000) }

  const openModal = (type, item = null) => { setModalType(type); setSelectedItem(item); setFormData(item || {}); setShowModal(true); setMessage({ type: "", text: "" }) }

  const closeModal = () => { setShowModal(false); setModalType(""); setSelectedItem(null); setFormData({}); setLoading(false) }

  const handleInputChange = (e) => { const { name, value, type, checked } = e.target; setFormData(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value })) }

   const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      let endpoint;
      if (modalType === "expense") {
        endpoint = selectedItem ? `/finance/expenses/${selectedItem._id || selectedItem.id}` : `/finance/expenses`;
      } else if (modalType === "budget") {
        endpoint = selectedItem ? `/finance/budgets/${selectedItem._id || selectedItem.id}` : `/finance/budgets`;
      } else {
        endpoint = selectedItem ? `/${modalType}s/${selectedItem._id || selectedItem.id}` : `/${modalType}s`;
      }
      console.log("Submitting to:", API_URL + endpoint, "modalType:", modalType);
      
      let body, headers;
      if (modalType === "expense" && formData.attachment) {
        // Send as FormData for file upload
        const formDataObj = new FormData();
        Object.keys(formData).forEach(key => {
          if (key === "attachment" && formData[key]) {
            formDataObj.append("attachments", formData[key]);
          } else if (formData[key] !== undefined && formData[key] !== null) {
            formDataObj.append(key, formData[key]);
          }
        });
        body = formDataObj;
        headers = { "Authorization": `Bearer ${localStorage.getItem("token")}` };
      } else {
        body = JSON.stringify(formData);
        headers = { "Content-Type": "application/json", "Authorization": `Bearer ${localStorage.getItem("token")}` };
      }
      
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: selectedItem ? "PUT" : "POST",
        headers,
        body
      });
      if (response.ok) { showMessage("success", `${modalType} saved successfully!`); fetchBudgets(); fetchExpenses(); fetchFinancialSummary(); setTimeout(() => closeModal(), 1500); }
      else { const error = await response.json().catch(() => ({ message: "Unknown error" })); showMessage("error", error.message || "Operation failed"); }
    } catch (err) { console.error("Submit error:", err); showMessage("error", "Network error. Please try again."); }
    finally { setLoading(false); }
  }

  const handleDelete = async (item, type) => {
    if (!window.confirm(`Delete this ${type}?`)) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/finance/${type}s/${item._id || item.id}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${localStorage.getItem("token")}` }
      });
      if (response.ok) { showMessage("success", `${type} deleted!`); fetchBudgets(); fetchExpenses(); fetchFinancialSummary(); }
      else { showMessage("error", "Delete failed"); }
    } catch (err) { showMessage("error", "Network error."); }
    finally { setLoading(false); }
  }
  const handleDeleteAttachment = async (expenseId, attachmentIndex) => {
    if (!window.confirm('Delete this attachment?')) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/finance/expenses/${expenseId}/attachment/${attachmentIndex}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        showMessage('success', 'Attachment deleted!');
        fetchExpenses();
      } else {
        showMessage('error', 'Failed to delete attachment');
      }
    } catch (err) {
      showMessage('error', 'Network error');
    } finally {
      setLoading(false);
    }
  };

  const recentActivity = [
    ...(expenses?.map(e => ({ ...e, type: 'expense', date: e.date, status: e.status })) || []),
    ...(budgets?.map(b => ({ ...b, type: 'budget', date: b.createdAt, status: b.status, description: `${b.category} Budget` })) || [])
  ].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 5)

  return (
    <div className="finance">
      {/* Module Navigation */}
      <div className="module-nav" style={{display: "flex", gap: "10px", marginBottom: "20px", padding: "15px", background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", borderRadius: "8px", flexWrap: "wrap"}}>
        <button onClick={() => navigate("/finance")} style={{padding: "10px 20px", background: "#fff", color: "#667eea", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "bold"}}>💰 Overview</button>
        <button onClick={() => setActiveTab('invoices')} style={{padding: "10px 20px", background: "#e91e63", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "bold"}}>🧾 Invoices</button>
        <button onClick={() => setActiveTab('receipts')} style={{padding: "10px 20px", background: "#4caf50", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "bold"}}>📄 Receipts</button>
        <button onClick={() => setActiveTab('quotations')} style={{padding: "10px 20px", background: "#ff9800", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "bold"}}>📋 Quotations</button>
      </div>

      {/* Header */}
      <div className="page-header">
        <div>
          <h1>Finance</h1>
          <p>Budget management, expense tracking, invoices, and financial reports</p>
        </div>
        <button className="btn btn-primary">
          <Plus size={18} />
          New Transaction
        </button>
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button 
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <TrendingUp size={18} />
          Overview
        </button>
        <button 
          className={`tab ${activeTab === 'invoices' ? 'active' : ''}`}
          onClick={() => setActiveTab('invoices')}
        >
          <Receipt size={18} />
          Invoices
        </button>
        <button 
          className={`tab ${activeTab === 'budget' ? 'active' : ''}`}
          onClick={() => setActiveTab('budget')}
        >
          <DollarSign size={18} />
          Budget
        </button>
        <button 
          className={`tab ${activeTab === 'expenses' ? 'active' : ''}`}
          onClick={() => setActiveTab('expenses')}
        >
          <FileText size={18} />
          Expenses
        </button>
        <button
          className={`tab ${activeTab === 'receipts' ? 'active' : ''}`}
          onClick={() => setActiveTab('receipts')}
        >
          <Receipt size={18} />
          Receipts
        </button>
        <button
          className={`tab ${activeTab === 'quotations' ? 'active' : ''}`}
          onClick={() => setActiveTab('quotations')}
        >
          <FileText size={18} />
          Quotations
        </button>
        <button
          className={`tab ${activeTab === 'reports' ? 'active' : ''}`}
          onClick={() => setActiveTab('reports')}
        >
          <Download size={18} />
          Reports
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'overview' && (
          <div className="overview-section">
            {/* Stats Grid - Modern Cool Tones */}
            <div className="stats-grid">
                              <div className="stat-card primary">
                <div className="stat-icon">
                  <TrendingUp size={24} />
                </div>
                <div className="stat-value">KES {(summary?.totalBudget || 0).toLocaleString()}</div>
                <div className="stat-label">Total Budget</div>
                <div className="stat-change up">
                  <TrendingUp size={14} /> {summary?.year || new Date().getFullYear()}
                </div>
              </div>
              <div className="stat-card danger">
                <div className="stat-icon">
                  <TrendingDown size={24} />
                </div>
                <div className="stat-value">KES {(summary?.totalExpenses || 0).toLocaleString()}</div>
                <div className="stat-label">Total Expenses</div>
                <div className="stat-change down">
                  <TrendingDown size={14} /> {(summary?.expensesByCategory || []).length} categories
                </div>
              </div>
              <div className="stat-card success">
                <div className="stat-icon">
                  <DollarSign size={24} />
                </div>
                <div className="stat-value">KES {(summary?.remaining || 0).toLocaleString()}</div>
                <div className="stat-label">Remaining Balance</div>
                <div className="stat-change up">
                  <TrendingUp size={14} /> {(summary?.remaining || 0) > 0 ? 'Healthy' : 'Over Budget'}
                </div>
              </div>
              <div className="stat-card info">
                <div className="stat-icon">
                  <Receipt size={24} />
                </div>
                <div className="stat-value">{summary?.totalInvoices || 0}</div>
                <div className="stat-label">Total Invoices</div>
                <div className="stat-change up">
                  <TrendingUp size={14} /> Active
                </div>
              </div>
            
            </div>

            {/* Recent Activity Section */}
            <div className="dashboard-section">
              <div className="section-header">
                <div>
                  <h3>Recent Financial Activity</h3>
                  <p>Latest transactions and updates</p>
                </div>
                <button className="btn btn-secondary" onClick={() => setActiveTab('reports')}>
                  View All Reports
                </button>
              </div>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Description</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {recentActivity.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="empty-state">
                        <FileText size={32} />
                        <p>No recent activity</p>
                      </td>
                    </tr>
                  ) : (
                    recentActivity.map((item, idx) => (
                      <tr key={idx}>
                        <td>
                          <span className={`badge badge-${item.type === 'income' ? 'success' : item.type === 'expense' ? 'danger' : 'info'}`}>
                            {item.type}
                          </span>
                        </td>
                        <td>{item.description || 'N/A'}</td>
                        <td className={`amount ${item.type === 'income' ? 'amount-positive' : 'amount-negative'}`}>
                          KES {item.amount?.toLocaleString() || 0}
                        </td>
                        <td>
                          <span className={`badge badge-${item.status === 'paid' ? 'success' : item.status === 'pending' ? 'warning' : 'secondary'}`}>
                            {item.status || 'N/A'}
                          </span>
                        </td>
                        <td>{item.date ? new Date(item.date).toLocaleDateString('en-KE') : 'N/A'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Quick Actions */}
            <div className="cards-grid">
              <div className="info-card">
                <h4>Budget Utilization</h4>
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ 
                      width: `${Math.min(100, (summary.totalExpenses / (summary.totalBudget || 1)) * 100)}%`,
                      background: (summary.totalExpenses / (summary.totalBudget || 1)) > 0.8 ? '#ef4444' : '#3b82f6'
                    }}
                  />
                </div>
                <p style={{ fontSize: '13px', color: '#64748b', marginTop: '8px' }}>
                  {((summary.totalExpenses / (summary.totalBudget || 1)) * 100).toFixed(1)}% of total budget used
                </p>
              </div>
              <div className="info-card">
                <h4>Quick Actions</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <button className="btn btn-primary" onClick={() => setActiveTab('invoices')}>
                    <Receipt size={16} /> Create Invoice
                  </button>
                  <button className="btn btn-secondary" onClick={() => setActiveTab('expenses')}>
                    <FileText size={16} /> Add Expense
                  </button>
                  <button className="btn btn-secondary" onClick={() => setActiveTab('budget')}>
                    <DollarSign size={16} /> Manage Budget
                  </button>
                </div>
              </div>
            </div>

            {/* Charts Grid */}
            <div className="charts-grid">
              <div className="chart-card">
                <h3>Expenses by Category</h3>
                <div className="category-list">
                  {summary.expensesByCategory?.map((cat, idx) => (
                    <div key={idx} className="category-item">
                      <span className="category-name">{cat._id}</span>
                      <div className="category-bar">
                        <div 
                          className="category-fill" 
                          style={{ width: `${(cat.total / summary.totalExpenses * 100) || 0}%` }}
                        ></div>
                      </div>
                      <span className="category-value">KES {cat.total.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="chart-card">
                <h3>Monthly Trend</h3>
                <div className="monthly-list">
                  {summary.monthlyExpenses?.map((month, idx) => (
                    <div key={idx} className="month-item">
                      <span>Month {month._id}</span>
                      <span>KES {month.total.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'invoices' && <InvoiceModule />}
        {activeTab === 'receipts' && <ReceiptModule />}
        {activeTab === 'quotations' && <QuotationModule />}

        {activeTab === 'budget' && (
          <div className="budget-section">
            <div className="section-toolbar">
              <div className="search-box">
                <Search size={18} />
                <input type="text" placeholder="Search budget..." />
              </div>
              <button className="btn btn-primary" onClick={() => openModal('budget')}>
                <Plus size={18} />
                Add Budget
              </button>
            </div>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Year</th>
                    <th>Category</th>
                    <th>Allocated</th>
                    <th>Spent</th>
                    <th>Remaining</th>
                    <th>Status</th> 
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                                    {budgets.map(budget => {
                    const pct = budget.percentageUsed || 0;
                    const status = budget.budgetStatus || 'healthy';
                    const statusColors = {
                      'healthy': '#4caf50',
                      'caution': '#8bc34a',
                      'warning': '#ff9800',
                      'critical': '#ff5722',
                      'over-budget': '#f44336'
                    };
                    const statusBg = {
                      'healthy': '#e8f5e9',
                      'caution': '#f1f8e9',
                      'warning': '#fff3e0',
                      'critical': '#fbe9e7',
                      'over-budget': '#ffebee'
                    };
                    return (
                      <tr key={budget._id}>
                        <td>{budget.year}</td>
                        <td><strong style={{textTransform: 'capitalize'}}>{budget.category}</strong></td>
                        <td>KES {(budget.allocated || 0).toLocaleString()}</td>
                        <td>KES {(budget.spent || 0).toLocaleString()}</td>
                        <td>
                          <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                            <div style={{
                              width: '80px',
                              height: '8px',
                              background: '#e0e0e0',
                              borderRadius: '4px',
                              overflow: 'hidden'
                            }}>
                              <div style={{
                                width: `${Math.min(100, pct)}%`,
                                height: '100%',
                                background: statusColors[status] || '#4caf50',
                                borderRadius: '4px',
                                transition: 'width 0.3s ease'
                              }} />
                            </div>
                            <span style={{fontSize: '12px', fontWeight: 600, color: statusColors[status]}}>
                              {pct.toFixed(1)}%
                            </span>
                          </div>
                        </td>
                        <td>KES {(budget.remainingAmount || 0).toLocaleString()}</td>
                        <td>
                          <span style={{
                            padding: '4px 12px',
                            borderRadius: '20px',
                            fontSize: '12px',
                            fontWeight: 600,
                            background: statusBg[status] || '#e8f5e9',
                            color: statusColors[status] || '#4caf50'
                          }}>
                            {budget.budgetStatusLabel || 'Under Budget'}
                          </span>
                        </td>
                        <td>
                          <span className={`status-badge ${(budget.status || 'draft').toLowerCase()}`}>
                            {budget.status || 'Draft'}
                          </span>
                        </td>
                        <td>
                          <button className="btn btn-icon" onClick={() => openModal('budget', budget)} title="Edit"><Edit size={14} /></button>
                          <button className="btn btn-icon btn-danger" onClick={() => handleDelete(budget, 'budget')} title="Delete"><Trash2 size={14} /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'expenses' && (
          <div className="expenses-section">
            <div className="section-toolbar">
              <div className="search-box">
                <Search size={18} />
                <input type="text" placeholder="Search expenses..." />
              </div>
              <button className="btn btn-primary" onClick={() => openModal('expense')}>
                <Plus size={18} />
                Add Expense
              </button>
            </div>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Date</th>
                    <th>Category</th>
                    <th>Description</th>
                    <th>Amount</th>
                    <th>Attachment</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                               
                </thead>
                <tbody>
                  {expenses.map(expense => (
                    <tr key={expense._id}>
                      <td className="font-mono">{expense.expenseId}</td>
                      <td>{new Date(expense.date).toLocaleDateString()}</td>
                      <td>{expense.category}</td>
                      <td>{expense.description}</td>
                      <td>KES {expense.amount.toLocaleString()}</td>
                                
                                            <td>
                        {expense.attachments && expense.attachments.length > 0 ? (
                          <div className="attachment-links">
                            {expense.attachments.map((att, idx) => (
                              <div key={idx} className="attachment-item">
                                <a href={`https://www.secs.gismac.co.ke${att.url}`} target="_blank" rel="noopener noreferrer" className="attachment-link" title={att.name}>
                                  📎 {att.name.length > 15 ? att.name.substring(0, 15) + '...' : att.name}
                                </a>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="no-attachment">—</span>
                        )}
                      </td>
                      <td>
                        <span className={`status-badge ${expense.status}`}>
                          {expense.status}
                        </span>
                      </td>
                      <td>
                        <button className="btn-icon" onClick={() => setPrintExpense(expense)} title="Print">🖨️</button>
                        <button className="btn-icon" onClick={() => openModal('expense', expense)} title="Edit">
                          <Edit size={16} />
                        </button>
                        <button className="btn-icon btn-danger" onClick={() => handleDelete(expense, 'expense')} title="Delete">
                          <Trash2 size={16} />
                        </button>
                      </td>
                      <td>
                        <span className={`status-badge ${expense.status}`}>
                          {expense.status}
                        </span>
                      </td>
                      <td>
                        <button className="btn-icon" onClick={() => setPrintExpense(expense)} title="Print">🖨️</button>
                        {isAdmin && (
                          <>
                            <button className="btn-icon" onClick={() => openModal('expense', expense)} title="Edit">
                              <Edit size={16} />
                            </button>
                            <button className="btn-icon btn-danger" onClick={() => handleDelete(expense, 'expense')} title="Delete">
                              <Trash2 size={16} />
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'reports' && <ReportModule />}
      </div>

      {message.text && !showModal && (
        <div className={`alert alert-${message.type} mb-3`}>
          {message.type === "success" ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
          {message.text}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && closeModal()}>
          <div className="modal-content">
            <div className="modal-header">
              <h3>{selectedItem ? "Edit" : "Add"} {modalType}</h3>
              <button onClick={closeModal}><X size={20} /></button>
            </div>
            {message.text && <div className={`alert alert-${message.type}`}>{message.text}</div>}
            <form onSubmit={handleSubmit}>
              {modalType === "budget" && (
                <div className="form-grid">
                  <div className="form-group"><label>Year</label><input type="number" name="year" value={formData.year || new Date().getFullYear()} onChange={handleInputChange} required /></div>
                  <div className="form-group"><label>Category</label><input type="text" name="category" value={formData.category || ""} onChange={handleInputChange} required /></div>
                  <div className="form-group"><label>Allocated Amount</label><input type="number" name="allocated" value={formData.allocated || ""} onChange={handleInputChange} required /></div>
                  <div className="form-group"><label>Description</label><textarea name="description" value={formData.description || ""} onChange={handleInputChange} rows="3" /></div>
                  <div className="form-group"><label>Status</label><select name="status" value={formData.status || "draft"} onChange={handleInputChange}>
                    <option value="draft">Draft</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select></div>
                </div>
              )}
              {modalType === "expense" && (
                <div className="form-grid">
                  <div className="form-group"><label>Date</label><input type="date" name="date" value={formData.date ? formData.date.split("T")[0] : ""} onChange={handleInputChange} required /></div>
                  <div className="form-group"><label>Category</label><select name="category" value={formData.category || "operations"} onChange={handleInputChange}>
                    <option value="operations">Operations</option>
                    <option value="salaries">Salaries</option>
                    <option value="equipment">Equipment</option>
                    <option value="transport">Transport</option>
                    <option value="utilities">Utilities</option>
                    <option value="stationary">Stationary</option>
                    <option value="system_development">System Development (IT)</option>
                    <option value="miscellaneous">Miscellaneous</option>
                    <option value="marketing">Marketing</option>
                    <option value="certification_compliance">Certification & Compliance</option>
                    <option value="statutory_deduction">Statutory Deduction</option>
                    <option value="uniforms">Uniforms</option>
                    <option value="assets">Assets</option>
                    <option value="entertainment">Entertainment</option>
                    <option value="other">Other</option>
                  </select></div>
                                    <div className="form-group"><label>Sub Category</label><input type="text" name="subCategory" value={formData.subCategory || ""} onChange={handleInputChange} placeholder="e.g., Boots, Trousers, Shirt" /></div>
                  <div className="form-group"><label>Description</label><input type="text" name="description" value={formData.description || ""} onChange={handleInputChange} required /></div>
                  <div className="form-group"><label>Amount</label><input type="number" name="amount" value={formData.amount || ""} onChange={handleInputChange} required /></div>
                  <div className="form-group"><label>Payment Method</label><select name="paymentMethod" value={formData.paymentMethod || "cash"} onChange={handleInputChange}>
                    <option value="cash">Cash</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="mpesa">M-Pesa</option>
                    <option value="cheque">Cheque</option>
                    <option value="card">Card</option>
                  </select></div>
                  <div className="form-group"><label>Supplier</label><input type="text" name="supplier" value={formData.supplier || ""} onChange={handleInputChange} placeholder="Enter supplier name" /></div>
                                    <div className="form-group">
                    <label>Attachment (Receipt)</label>
                    {selectedItem && selectedItem.attachments && selectedItem.attachments.length > 0 && (
                      <div className="existing-attachments">
                        <small>Existing:</small>
                        {selectedItem.attachments.map((att, idx) => (
                          <a key={idx} href={`https://www.secs.gismac.co.ke${att.url}`} target="_blank" rel="noopener noreferrer" className="existing-attachment-link">
                            📎 {att.name}
                          </a>
                        ))}
                      </div>
                    )}
                    <input type="file" name="attachment" onChange={(e) => setFormData({...formData, attachment: e.target.files[0]})} />
                    <small style={{color: '#888'}}>Upload new file to add to existing attachments</small>
                  </div>
                                    <div className="form-group"><label>Created By</label><input type="text" name="createdBy" value={formData.createdBy || ""} onChange={handleInputChange} placeholder="Enter officer name" /></div>
                  <div className="form-group"><label>Status</label><select name="status" value={formData.status || "pending"} onChange={handleInputChange}>
                    <option value="draft">Draft</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select></div>
                </div>
              )}
              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={closeModal} disabled={loading}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? "Saving..." : (selectedItem ? "Update" : "Save")}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {printBudget && (
        <PrintBudget budget={printBudget} onClose={() => setPrintBudget(null)} />
      )}
      {printExpense && (
        <PrintExpense expense={printExpense} onClose={() => setPrintExpense(null)} />
      )}

    </div>
  )
}
